// Healing & Treatments - Core Page (Using client's exact wording)
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, MapPin } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { ClickableImage } from "../components/shared/ImageLightbox";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../components/shared/SEOHelper";
import { ReviewsStrip } from "../components/shared/Reviews";
import { BookwhenEmbed } from "../components/shared/BookwhenEmbed";

const TreatmentsPage = () => {
  usePageMeta({
    title: "Healing & Treatments | Massage, Reiki & Energy Healing",
    description: "Massage, energy healing and intuitive guidance to support deep rest, release and transformation. A space where your body can soften, your mind can settle",
    canonical: "https://annelielily.com/treatments"
  });

  const serviceSchema = generateServiceSchema({
    name: "Healing & Treatments",
    description: "Massage, energy healing and intuitive guidance with Annelie Lily in Frome and Exeter.",
    serviceType: "WellnessService",
    url: "https://annelielily.com/treatments"
  });

  // What this work supports (from client's email)
  const whatThisSupports = [
    "Feeling overwhelmed, anxious or emotionally heavy",
    "Holding stress, tightness or tension in the body",
    "Moving through change, uncertainty or transition and want to rebalance and realign your life",
    "Feeling disconnected from yourself",
    "Noticing patterns you are ready to shift"
  ];

  // What you may experience (from client's email)
  const whatYouMayExperience = [
    "Deep physical relaxation through massage",
    "A release of tension or stored stress",
    "Emotional shifts or clarity",
    "A sense of grounding and reconnection",
    "A feeling of your energy being lighter, clearer or more settled",
    "Insight or guidance from the cards"
  ];

  // Signature Treatment 1 elements - with links to child pages
  const signatureTreatment1Elements = [
    { name: "Massage", desc: "to release physical tension and calm the nervous system", link: "/treatments/swedish-massage" },
    { name: "Reiki and energy healing", desc: "to support balance and flow", link: "/treatments/reiki-healing" },
    { name: "Chakra healing", desc: "to gently realign your energy centres", link: "/treatments/chakra-healing" },
    { name: "Crystal healing", desc: "to support and deepen the process", link: null },
    { name: "Health & Nutrition", desc: "guidance to help support you on your healing journey", link: null },
    { name: "Intuitive Guidance", desc: "and/or guidance cards used to offer insight and reflection", link: null }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Treatments" currentSlug="treatments" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Healing & Treatments
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              A Space to Rest, Release & Reconnect
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              From a tranquil garden room on the outskirts of Frome, I offer massage, energy healing and intuitive guidance to support deep rest, release and transformation.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Clock className="w-4 h-4 text-gold" />
                60 / 75 / 90 min
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Frome / Exeter
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="treatments-book-btn"
              >
                Book Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-secondary"
                data-testid="treatments-enquire-btn"
              >
                Ask a Question
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Book a Treatment - Bookwhen (top placement) */}
      <BookwhenEmbed
        slug="annelielily14"
        title="Book a Treatment"
        subtitle="Choose a session below and book your place in seconds."
        bgClass="bg-cream"
        testId="treatments-bookwhen"
      />

      {/* Opening Section - New top photo of Annelie */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-gold/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/2jysvlly_A3980602-9EFE-4B7D-9DDD-F76B02671369.PNG"
                  alt="Annelie Lily holding a crystal pendulum during a healing session, bathed in warm golden hour light on terracotta steps"
                  className="w-full h-full object-cover"
                  width={800}
                  height={1000}
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="hidden md:block absolute -top-3 -left-3 w-6 h-6 bg-gold/30 rounded-full" />
              <div className="hidden md:block absolute -bottom-4 -right-4 w-10 h-10 bg-gold/20 rounded-full" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="prose-content space-y-4 text-neutral-700">
                <p>
                  There are times in life when something doesn't feel quite right.
                </p>
                <p>
                  You may feel overwhelmed, stuck, disconnected, or carrying something you can't fully put into words.
                </p>
                <p className="text-lg font-medium text-neutral-800">
                  My treatments offer you space to pause.
                </p>
                <p>
                  A space where your body can soften, your mind can settle, and you can begin to reconnect with yourself.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Treatment Space Gallery - moved up to right under top photo */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              The Treatment Space
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              A Tranquil Garden Room in Frome
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              A calm, nurturing environment designed for deep relaxation and healing.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <ClickableImage
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/i2hmz3ig_IMG_1863.jpeg"
                  alt="Tranquil garden room treatment space in Frome with massage table dressed in rainbow chakra blanket and natural light"
                  imgClassName="w-full h-full object-cover"
                  width={600}
                  height={600}
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <ClickableImage
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/ppvrp6n8_IMG_1862.jpeg"
                  alt="Full view of the peaceful garden room treatment space with skylight, comfortable seating area and massage table"
                  imgClassName="w-full h-full object-cover"
                  width={600}
                  height={600}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Signature Treatments */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Signature Treatments
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800">
              Two Ways to Experience Deep Healing
            </h2>
          </motion.div>

          {/* Signature Treatment 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10 mb-6"
          >
            <h3 className="font-heading text-xl md:text-2xl text-neutral-800 mb-4">
              Signature Treatment 1 — Massage, Reiki, Chakra Healing with Intuitive Guidance
            </h3>
            <p className="text-neutral-700 mb-6">
              This is my signature offering — a deeply supportive treatment that works with both the body and your energy.
            </p>
            <p className="text-neutral-700 mb-4">
              Each session weaves together:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {signatureTreatment1Elements.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 bg-gold/5 p-4 rounded-xl border border-gold/10"
                >
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    {item.link ? (
                      <Link to={item.link} className="text-neutral-800 font-medium hover:text-gold transition-colors underline underline-offset-2">
                        {item.name}
                      </Link>
                    ) : (
                      <span className="text-neutral-800 font-medium">{item.name}</span>
                    )}
                    <span className="text-neutral-600"> {item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-neutral-600 italic text-sm text-center mt-2 mb-2">
              All of the above can be booked as individual treatments and sessions.
            </p>
          </motion.div>

          {/* Signature Treatment 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10"
          >
            <h3 className="font-heading text-xl md:text-2xl text-neutral-800 mb-4">
              Signature Treatment 2 — Aura Transformation
            </h3>
            <p className="text-neutral-700 mb-4">
              To clear, reset and strengthen your energetic field. This is a one-off powerful life changing treatment 
              which takes place over two sessions.
            </p>
            <Link 
              to="/contact" 
              className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              Enquire about Aura Transformation <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* The Unique Nature */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="prose-content space-y-4 text-neutral-700 text-center">
              <p className="text-lg font-medium text-neutral-800">
                No two treatments from me are the same.
              </p>
              <p>
                Some are deeply restful and quiet.
              </p>
              <p>
                Others bring gentle clarity, emotional release or a sense of direction.
              </p>
              <p className="text-gold italic">
                The treatment meets you where you are.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What This Work Supports */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What This Work Supports
            </h2>
            <p className="text-neutral-700 mb-6">
              People come to these sessions for many different reasons. This work can support you if you are:
            </p>
            <ul className="space-y-3 mb-6">
              {whatThisSupports.map((item, index) => (
                <li key={index} className="flex items-start gap-3 bg-white/70 p-4 rounded-xl border border-gold/10">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-neutral-600 italic">
              You do not need to have a clear reason. Sometimes it is simply about creating space to rest and reset.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You May Experience */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What You May Experience
            </h2>
            <p className="text-neutral-700 mb-6">
              Each session is unique, but you may notice:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {whatYouMayExperience.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 bg-white/70 p-4 rounded-xl border border-gold/10"
                >
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-neutral-600 italic">
              Often the shifts are subtle, but meaningful.
            </p>
          </motion.div>
        </div>
      </section>

      {/* My Approach */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                My Approach
              </h2>
              <div className="prose-content space-y-4 text-neutral-700">
                <p>
                  My work is grounded, intuitive and supportive.
                </p>
                <p>
                  By combining massage with energy and intuitive work, I support both the body and the 
                  deeper layers held beneath it — including the energy field around you.
                </p>
                <p>
                  <strong>This is not about fixing or diagnosing.</strong>
                </p>
                <p>
                  It is about creating a safe, calm space where your system can settle, and where healing can happen naturally.
                </p>
                <p className="italic text-gold">
                  Everything is gentle, respectful and led at your pace.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/869znj15_IMG_1990.jpg"
                  alt="Annelie Lily standing welcomingly by the massage table in her peaceful treatment room in Frome"
                  className="w-full h-full object-contain bg-cream"
                  width={600}
                  height={600}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practical Details */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Practical Details
            </h2>
            <div className="bg-white/70 p-6 rounded-xl border border-gold/10 space-y-4">
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-neutral-800">Session Length</p>
                  <p className="text-neutral-600">60 / 75 / 90 minutes</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-neutral-800">Location</p>
                  <p className="text-neutral-600">Frome / Exeter / In-person</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-5 h-5 text-gold flex-shrink-0 mt-1 flex items-center justify-center font-medium">£</span>
                <div>
                  <p className="font-medium text-neutral-800">Prices</p>
                  <p className="text-neutral-600">Starting from £65</p>
                </div>
              </div>
              <p className="text-neutral-500 text-sm pt-2 border-t border-gold/10">
                Please wear comfortable clothing — massage is always adapted to your comfort level.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This is For */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Who This is For
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                These sessions are open to both men and women.
              </p>
              <p>
                Whether you are drawn to the massage, the healing, or simply the space to pause — you are very welcome.
              </p>
              <p>
                <strong>No previous experience is needed.</strong>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-neutral-800 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Ready to Rest, Reconnect & Receive?
            </h2>
            <p className="text-neutral-300 mb-8 max-w-xl mx-auto">
              If you feel ready for some space to reconnect, rest and receive, you are very welcome.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-gold hover:bg-gold-dark text-white px-8 py-3.5 rounded-full font-medium transition-colors inline-flex items-center justify-center gap-2"
              >
                Book a Treatment <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/contact" 
                className="border border-white/30 hover:border-white text-white px-8 py-3.5 rounded-full font-medium transition-colors"
              >
                Get in Touch with Any Questions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsStrip pageType="treatments" title="What Our Clients Say" />

      {/* Internal Links for SEO */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <p className="text-neutral-500 text-sm text-center mb-4">
            Explore specific treatments:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/treatments/reiki-healing" className="text-gold hover:underline text-sm">Reiki Healing</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments/chakra-healing" className="text-gold hover:underline text-sm">Chakra Healing</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments/swedish-massage" className="text-gold hover:underline text-sm">Swedish Massage</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments/holistic-treatments" className="text-gold hover:underline text-sm">View All Treatments</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TreatmentsPage;
